// Companion/chatConversationHydration.ts
//
// One fetched conversation as the `conversationHydrated` payload. Shared by
// `useChatConversations` and the QBank block chat (ORA-1285), which adopts the
// conversation the server returns when a thread becomes the block's.

import type { ChatHydratedBlock } from '@/slices/chat/chatSlice';
import type { ChatConversationDetail } from '@/services/chatConversations';
import { legacyToolBlocksToChatBlocks } from './blocks/legacyChatBlocks';

/**
 * Group the old panel's persisted tool blocks by the message they belonged to
 * and map them onto the block contract. Legacy conversations only; a block
 * whose kind has no modern counterpart is dropped by the mapper.
 */
const legacyBlocksByMessage = (
  detail: ChatConversationDetail
): Map<string, ChatHydratedBlock[]> => {
  const grouped = new Map<string, ChatHydratedBlock[]>();

  detail.legacyToolBlocks.forEach((block) => {
    const mapped = legacyToolBlocksToChatBlocks([block]);
    if (mapped.length === 0) return;

    grouped.set(block.messageId, [
      ...(grouped.get(block.messageId) ?? []),
      ...mapped,
    ]);
  });

  return grouped;
};

/** The `conversationHydrated` payload for one fetched conversation. */
export const toHydratedConversation = (detail: ChatConversationDetail) => {
  const legacyBlocks = detail.legacy
    ? legacyBlocksByMessage(detail)
    : new Map<string, ChatHydratedBlock[]>();

  return {
    id: detail.id,
    title: detail.title,
    legacy: detail.legacy,
    questionBlockId: detail.questionBlockId,
    messages: detail.messages.map((message) => ({
      id: message.id,
      role: message.role,
      text: message.text,
      attachments: message.attachments.map((attachment) => ({
        id: attachment.id,
        mimeType: attachment.mimeType,
        ...(attachment.kind ? { kind: attachment.kind } : {}),
        ...(attachment.documentId ? { documentId: attachment.documentId } : {}),
        ...(attachment.fileName ? { fileName: attachment.fileName } : {}),
        ...(attachment.pageCount !== undefined
          ? { pageCount: attachment.pageCount }
          : {}),
        previewUrl: attachment.url,
        ...(attachment.key ? { key: attachment.key } : {}),
        ...(attachment.sizeBytes !== undefined
          ? { sizeBytes: attachment.sizeBytes }
          : {}),
      })),
      citations: message.citations,
      blocks:
        message.blocks.length > 0
          ? message.blocks
          : (legacyBlocks.get(message.id) ?? []),
      // The trail and the chips the answer showed live (ORA-604, ORA-606).
      // Passed through as-is: the slice rebuilds the finished turn from them.
      ...(message.followups.length > 0 ? { followups: message.followups } : {}),
      ...(message.activity ? { activity: message.activity } : {}),
      ...(message.contextLabel ? { contextLabel: message.contextLabel } : {}),
      ...(message.actionProposal
        ? {
            actionProposal: {
              ...message.actionProposal,
              turnId: message.id,
            },
          }
        : {}),
    })),
  };
};
